import React, { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AddKolDbDataDialog } from "./AddKolDbDataDialog";
import SyncProfilesButton from "@/components/twitter/SyncProfilesButton";
import { useOptimisticUpdates } from "@/hooks/useOptimisticUpdates";
import { CONTENT_TYPE_OPTIONS } from './constants/kolDictionaryConstants';
import { 
  HierarchicalKolData,
  fetchPicOptions, 
  processRowUpdate 
} from './utils/kolDictionaryUtils';
import { useAuth } from "@/contexts/AuthContext";
import { KolHierarchicalDictionaryRow } from "./KolHierarchicalDictionaryRow";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { TableActionBar } from "@/components/table/TableActionBar";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTableSorting } from "@/hooks/useTableSorting";
import { SortableHeader } from "@/components/ui/sortable-header";
import { Tables } from "@/integrations/supabase/types";

interface KolDbTableProps {
  onDataChange?: () => void;
}

// Type for raw Supabase data
type RawKolDbData = Tables<'kol_db'>;

// Function to process raw Supabase data into our expected format
const processKolDbData = (data: RawKolDbData[]): HierarchicalKolData[] => {
  const processed = data.map(item => ({
    ...item,
    start_date: item.start_date ? item.start_date : null,
    end_date: item.end_date ? item.end_date : null,
    internal_kol_type: item.internal_kol_type || null,
    content_type: Array.isArray(item.content_type) 
      ? item.content_type.map(ct => typeof ct === 'string' ? ct : String(ct))
      : [],
    pic_id: Array.isArray(item.pic_id) ? item.pic_id : [],
    price_content_type: typeof item.price_content_type === 'object' && item.price_content_type !== null
      ? item.price_content_type as Record<string, number>
      : {},
    created_at: item.created_at || null,
    updated_at: item.updated_at || null,
  }));

  // Build hierarchical structure - only root items (no parent_id) should be at top level
  const hierarchical = buildHierarchy(processed);
  
  // Sort by created_at descending (latest first)
  return hierarchical.sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });
};

const buildHierarchy = (data: HierarchicalKolData[]): HierarchicalKolData[] => {
  const itemMap = new Map<string, HierarchicalKolData>();
  const rootItems: HierarchicalKolData[] = [];

  // First pass: create map of all items
  data.forEach(item => {
    itemMap.set(item.id, { ...item, children: [], isExpanded: false });
  });

  // Second pass: build parent-child relationships
  data.forEach(item => {
    const processedItem = itemMap.get(item.id)!;
    
    if (item.parent_id && itemMap.has(item.parent_id)) {
      // This is a child item - add it to parent's children
      const parent = itemMap.get(item.parent_id)!;
      parent.children!.push(processedItem);
    } else if (!item.parent_id) {
      // This is a root item (no parent_id) - add to root items
      rootItems.push(processedItem);
    }
    // Items with parent_id that don't have a matching parent are ignored
    // (they won't appear as root items)
  });

  return rootItems;
};

export function KolDbTable({ onDataChange }: KolDbTableProps) {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const { toast } = useToast();
  const [hierarchicalData, setHierarchicalData] = useState<HierarchicalKolData[]>([]);
  const [picOptions, setPicOptions] = useState<{value: string, label: string}[]>([]);
  const [contentTypeOptions, setContentTypeOptions] = useState<{value: string, label: string}[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  
  // Get auth context for BU access check
  const { hasBuAccess } = useAuth();

  // Initialize table sorting
  const { sortState, handleSort, sortedData } = useTableSorting({
    data: hierarchicalData,
    defaultSort: { column: null, direction: null }
  });

  // Optimistic updates for real-time feedback
  const {
    optimisticData,
    applyOptimisticUpdate,
    isPending,
    isProcessing: isOptimisticProcessing,
    hasPendingUpdates
  } = useOptimisticUpdates({
    data: sortedData,
    onUpdate: async (newRow: any, oldRow: any) => {
      try {
        setIsProcessing(true);
        const result = await processRowUpdate(newRow, oldRow, onDataChange);
        
        toast({
          title: "Success",
          description: "KOL DB record updated successfully",
        });
        
        return result;
      } catch (error) {
        console.error("Error updating KOL DB row:", error);
        toast({
          title: "Error",
          description: "Failed to update KOL DB record",
          variant: "destructive"
        });
        return oldRow;
      } finally {
        setIsProcessing(false);
      }
    },
    debounceMs: 500
  });

  const fetchData = async () => {
    if (hasBuAccess) return;
    
    try {
      setIsLoading(true);
      setHasError(false);
      console.log(`Fetching KOL DB data - Page: ${currentPage}, Page Size: ${pageSize}, Search: "${searchTerm}"`);
      
      // For "Show All" option, don't use pagination
      const usesPagination = pageSize !== -1;
      const itemsPerPage = usesPagination ? pageSize : 1000000;
      const offset = usesPagination ? (currentPage - 1) * pageSize : 0;
      
      // Build query with search
      let query = supabase
        .from('kol_db')
        .select('*', { count: 'exact' });
      
      // Add search filters if search term exists
      if (searchTerm.trim()) {
        query = query.or(`creator_name.ilike.%${searchTerm}%,social_media_handler.ilike.%${searchTerm}%,social_media_type.ilike.%${searchTerm}%,region.ilike.%${searchTerm}%`);
      }
      
      // Get count first
      const { count, error: countError } = await query;
      if (countError) {
        console.error("Error counting KOL DB records:", countError);
        throw countError;
      }
      
      setTotalCount(count || 0);
      
      // Get paginated data with same search filters
      let dataQuery = supabase
        .from('kol_db')
        .select('*')
        .order('created_at', { ascending: false });
      
      // Only apply range if using pagination
      if (usesPagination) {
        dataQuery = dataQuery.range(offset, offset + itemsPerPage - 1);
      }
      
      // Apply same search filters to data query
      if (searchTerm.trim()) {
        dataQuery = dataQuery.or(`creator_name.ilike.%${searchTerm}%,social_media_handler.ilike.%${searchTerm}%,social_media_type.ilike.%${searchTerm}%,region.ilike.%${searchTerm}%`);
      }
      
      const { data, error } = await dataQuery;
      if (error) {
        console.error("Error fetching KOL DB data:", error);
        throw error;
      }
      
      console.log(`Fetched ${data?.length || 0} KOL DB records`);
      
      // Process and sort data
      const processed = processKolDbData(data || []);
      const sortedData = processed ? [...processed].sort((a, b) => {
        const aIsTwitter = a.social_media_type?.toLowerCase() === 'twitter' || a.social_media_type?.toLowerCase() === 'x';
        const bIsTwitter = b.social_media_type?.toLowerCase() === 'twitter' || b.social_media_type?.toLowerCase() === 'x';
        
        if (aIsTwitter && !bIsTwitter) return -1;
        if (!aIsTwitter && bIsTwitter) return 1;
        return 0;
      }) : [];
      
      setHierarchicalData(sortedData);
    } catch (error: any) {
      console.error("Error in fetchData:", error);
      setHasError(true);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      toast({
        title: "Error",
        description: `Failed to load KOL Dictionary data: ${errorMessage}`,
        variant: "destructive"
      });
      setHierarchicalData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeOptions = async () => {
      const pics = await fetchPicOptions();
      setPicOptions(pics);
      
      const contentOptions = CONTENT_TYPE_OPTIONS.map(type => ({
        value: type,
        label: type
      }));
      setContentTypeOptions(contentOptions);
    };
    
    initializeOptions();
  }, []);

  useEffect(() => {
    if (!hasBuAccess) {
      const debounceTimer = setTimeout(() => {
        fetchData();
      }, 300);
      return () => clearTimeout(debounceTimer);
    }
  }, [currentPage, pageSize, searchTerm, hasBuAccess]);

  // Reset to page 1 when sorting changes
  useEffect(() => {
    if (sortState.column) {
      setCurrentPage(1);
    }
  }, [sortState]);

  const handleDeleteSelected = async () => {
    try {
      const { error } = await supabase
        .from('kol_db')
        .delete()
        .in('id', selectedRows);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${selectedRows.length} record(s) deleted successfully`,
      });

      setSelectedRows([]);
      
      if (onDataChange) onDataChange();
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error deleting rows:", error);
      toast({
        title: "Error",
        description: "Failed to delete selected records",
        variant: "destructive"
      });
    }
  };

  const handleUpdateContentTypes = async (rowId: string, newValues: string[]) => {
    try {
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('kol_db')
        .update({ content_type: newValues })
        .eq('id', rowId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Content types updated successfully",
      });
      
      if (onDataChange) onDataChange();
      fetchData(); // Refresh data
      
    } catch (error: any) {
      console.error("Error updating content types:", error);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      toast({
        title: "Error",
        description: `Failed to update content types: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdatePIC = async (rowId: string, newValues: string[]) => {
    try {
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('kol_db')
        .update({ pic_id: newValues })
        .eq('id', rowId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "PIC updated successfully",
      });
      
      if (onDataChange) onDataChange();
      fetchData(); // Refresh data
      
    } catch (error: any) {
      console.error("Error updating PIC:", error);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      toast({
        title: "Error",
        description: `Failed to update PIC: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateKolType = async (rowId: string, newValue: string) => {
    try {
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('kol_db')
        .update({ kol_type: newValue })
        .eq('id', rowId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "KOL Type updated successfully",
      });
      
      if (onDataChange) onDataChange();
      fetchData(); // Refresh data
      
    } catch (error: any) {
      console.error("Error updating KOL Type:", error);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      toast({
        title: "Error",
        description: `Failed to update KOL Type: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateInternalKolType = async (rowId: string, newValue: string) => {
    try {
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('kol_db')
        .update({ internal_kol_type: newValue })
        .eq('id', rowId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Internal KOL Type updated successfully",
      });
      
      if (onDataChange) onDataChange();
      fetchData(); // Refresh data
      
    } catch (error: any) {
      console.error("Error updating Internal KOL Type:", error);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      toast({
        title: "Error",
        description: `Failed to update Internal KOL Type: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateRegion = async (rowId: string, newValue: string) => {
    try {
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('kol_db')
        .update({ region: newValue })
        .eq('id', rowId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Region updated successfully",
      });
      
      if (onDataChange) onDataChange();
      fetchData(); // Refresh data
      
    } catch (error: any) {
      console.error("Error updating Region:", error);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      toast({
        title: "Error",
        description: `Failed to update Region: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateTier = async (rowId: string, newValue: string) => {
    try {
      setIsProcessing(true);
      
      const { error } = await supabase
        .from('kol_db')
        .update({ tier: newValue })
        .eq('id', rowId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Tier updated successfully",
      });
      
      if (onDataChange) onDataChange();
      fetchData(); // Refresh data
      
    } catch (error: any) {
      console.error("Error updating Tier:", error);
      const errorMessage = error.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      toast({
        title: "Error",
        description: `Failed to update Tier: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const totalPages = pageSize === -1 ? 1 : Math.ceil(totalCount / pageSize);
  const showPagination = pageSize !== -1 && totalCount > pageSize;

  if (hasError) {
    return (
      <div className="p-8 text-center border border-dashed rounded-md bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
        <h3 className="text-lg font-medium text-red-900 dark:text-red-100">
          Failed to load data
        </h3>
        <p className="text-red-700 dark:text-red-300 mt-2">
          There was an error loading the data. Please check your connection and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 h-[calc(100vh-12rem)] flex flex-col">
      {/* Action Bar */}
      <div className="flex justify-between items-center gap-3 p-3 bg-card/50 rounded-lg border border-border/50 flex-shrink-0">
        <div className="flex items-center gap-3">
          {!hasBuAccess ? (
            <>
              <AddKolDbDataDialog onDataAdded={() => { if (onDataChange) onDataChange(); fetchData(); }} />
              <SyncProfilesButton onSyncComplete={() => { if (onDataChange) onDataChange(); fetchData(); }} />
            </>
          ) : (
            <div className="text-sm text-red-600 font-medium bg-red-50 px-3 py-1 rounded-md">
              BU Access - Actions Disabled
            </div>
          )}
          
          <TableActionBar 
            selectedRowsCount={selectedRows.length} 
            onDeleteSelected={handleDeleteSelected}
          />
          
          {(isProcessing || isOptimisticProcessing) && (
            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span>Processing...</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Search Bar */}
          <div className="relative w-[250px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search KOL Dictionary..."
              className="pl-8 bg-background border-input text-foreground"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Table Container with constrained height */}
      <div className="border rounded-lg bg-card flex flex-col flex-1 min-h-0">
        {/* Table with scrollable content */}
        <div className="flex-1 relative overflow-hidden">
          <TableLoadingOverlay 
            isLoading={isProcessing || isOptimisticProcessing} 
          />
          
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : optimisticData.length === 0 ? (
            <div className="p-8 text-center border-b border-dashed bg-muted/20">
              <h3 className="text-lg font-medium">
                {searchTerm ? "No matching results found" : "No KOL Dictionary data found"}
              </h3>
              <p className="text-muted-foreground mt-2">
                {searchTerm 
                  ? `No results found for "${searchTerm}". Try adjusting your search term.`
                  : "Add some KOL data to get started or check your access permissions"
                }
              </p>
              {!searchTerm && !hasBuAccess && (
                <div className="mt-4">
                  <AddKolDbDataDialog onDataAdded={() => { if (onDataChange) onDataChange(); fetchData(); }} />
                </div>
              )}
            </div>
          ) : (
            <div className="h-full overflow-y-auto">
              <table className="w-full border-collapse bg-background">
                <thead className="sticky top-0 z-10 bg-muted/50">
                  <tr className="border-b">
                    <th className="w-[40px] px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {/* Expand/Collapse column */}
                    </th>
                    <th className="border-r border-border/50">
                      <SortableHeader
                        column="creator_name"
                        currentSort={sortState}
                        onSort={handleSort}
                      >
                        Creator Name
                      </SortableHeader>
                    </th>
                    <th className="border-r border-border/50">
                      <SortableHeader
                        column="kol_type"
                        currentSort={sortState}
                        onSort={handleSort}
                      >
                        KOL Type
                      </SortableHeader>
                    </th>
                    <th className="border-r border-border/50">
                      <SortableHeader
                        column="internal_kol_type"
                        currentSort={sortState}
                        onSort={handleSort}
                      >
                        Internal KOL Type
                      </SortableHeader>
                    </th>
                    <th className="border-r border-border/50">
                      <SortableHeader
                        column="region"
                        currentSort={sortState}
                        onSort={handleSort}
                      >
                        Region
                      </SortableHeader>
                    </th>
                    <th className="border-r border-border/50">
                      <SortableHeader
                        column="tier"
                        currentSort={sortState}
                        onSort={handleSort}
                      >
                        Tier
                      </SortableHeader>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Content Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">PIC</th>
                  </tr>
                </thead>
                <tbody>
                  {optimisticData.map((row) => (
                     <KolHierarchicalDictionaryRow
                       key={row.id}
                       data={row}
                       onUpdate={async (newRow: any, oldRow: any) => {
                         return await processRowUpdate(newRow, oldRow, onDataChange);
                       }}
                       isPending={isPending}
                       isOptimisticProcessing={isOptimisticProcessing}
                       contentTypeOptions={contentTypeOptions}
                       picOptions={picOptions}
                       handleUpdateContentTypes={handleUpdateContentTypes}
                       handleUpdatePIC={handleUpdatePIC}
                       handleUpdateKolType={handleUpdateKolType}
                       handleUpdateInternalKolType={handleUpdateInternalKolType}
                       handleUpdateRegion={handleUpdateRegion}
                       handleUpdateTier={handleUpdateTier}
                       applyOptimisticUpdate={applyOptimisticUpdate}
                       level={0}
                     />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Fixed Pagination at bottom - only show when not searching and pagination is enabled */}
        {showPagination && !searchTerm.trim() && (
          <div className="border-t p-4 flex-shrink-0">
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              totalItems={totalCount}
              showPageSizeSelect={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default KolDbTable;
